import DOMPurify from 'dompurify';
import { api } from '../../core/network/api';

export interface LegalPage {
  id: string;
  slug: string;
  title: string;
  content: string;
  updatedAt: string;
}

export interface CompanyInfo {
  name: string;
  registrationNumber: string;
  address: string;
  email: string;
  jurisdiction: string;
  country: string;
  gameName: string;
}

/** GET /api/cms/legal/:slug — an active legal document from the core CMS (admin-managed). */
export const fetchLegalPage = (slug: string) => api.get<LegalPage>(`/cms/legal/${slug}`);

/** One row of the legal-document index — no body, that is fetched per slug. */
export interface LegalPageSummary {
  slug: string;
  title: string;
  updatedAt: string;
}

/**
 * GET /api/legal/pages — every legal document the admin has published.
 *
 * The landing footer links a fixed three (Terms / Privacy / Cookies); the
 * in-game Papers list instead tracks whatever is actually published, so a
 * document added in the admin panel shows up without a frontend change.
 */
export const fetchLegalPages = () => api.get<LegalPageSummary[]>('/legal/pages');

/** GET /api/legal/company — public legal-entity info (admin-managed `company.*` settings). */
export const fetchCompanyInfo = () => api.get<CompanyInfo>('/legal/company');

/**
 * Substitute `{{company.*}}` / `{{game.name}}` / `{{year}}` placeholders in
 * admin-authored legal HTML, so the documents track the legal entity
 * configured in Admin → Game Settings without copy edits.
 */
export function fillCompanyVars(html: string, c: CompanyInfo | null): string {
  const vars: Record<string, string> = {
    'company.name': c?.name || 'the Company',
    'company.registration': c?.registrationNumber || '—',
    'company.address': c?.address || '—',
    'company.email': c?.email || 'support@gamepreview.it.com',
    'company.jurisdiction': c?.jurisdiction || c?.country || '—',
    'company.country': c?.country || '—',
    'game.name': c?.gameName || 'Mafinini',
    year: String(new Date().getFullYear()),
  };
  const filled = html.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (m, key: string) => vars[key] ?? m);
  return DOMPurify.sanitize(filled, { ADD_ATTR: ['data-legal'] });
}
